import React, { memo } from "react";
import NodeWrapper from "./NodeWrapper";
import { Code } from "@mui/icons-material";
import { Handle, Position } from "@xyflow/react";
import useHandleConnected from "./useHandleConnected";

/**
 * Code Node — Executar JavaScript customizado (sandbox).
 *
 * data.code: string — codigo JS a executar
 * data.codeLanguage: "javascript" (default)
 *
 * Variaveis disponiveis no sandbox:
 *   - variables: objeto com todas as variaveis do flow
 *   - contact: { name, number, email }
 *   - ticket: { id, status, queueId }
 *   - input: ultima mensagem do usuario
 *
 * Retorno: resultado armazenado em data.outputVariable
 *
 * Saidas: handle "a" (sucesso), handle "error" (erro)
 */
const CodeNode = memo(({ id, data, isConnectable, selected }) => {
  const getHandleClass = useHandleConnected(id);
  const code = data?.code || "";
  const outputVar = data?.outputVariable || "";
  const lines = code.split("\n").length;

  return (
    <NodeWrapper
      id={id}
      data={data}
      icon={<Code />}
      title="Codigo"
      color="#212121"
      bgColor="#FAFAFA"
      noSource
      isConnectable={isConnectable}
      selected={selected}
      minWidth={230}
    >
      {/* Code preview */}
      <div style={{
        background: "#1e1e1e",
        borderRadius: 6,
        padding: "8px 10px",
        maxHeight: 80,
        overflow: "hidden",
        position: "relative",
      }}>
        <pre style={{
          margin: 0,
          fontSize: 10,
          fontFamily: "'Fira Code', 'Consolas', monospace",
          color: "#d4d4d4",
          lineHeight: 1.4,
          whiteSpace: "pre-wrap",
          wordBreak: "break-all",
        }}>
          {code ? code.substring(0, 120) : "// Sem codigo configurado"}
        </pre>
        {code.length > 120 && (
          <div style={{
            position: "absolute", bottom: 0, left: 0, right: 0,
            height: 20, background: "linear-gradient(transparent, #1e1e1e)",
          }} />
        )}
      </div>

      {/* Info */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginTop: 6, fontSize: 10, color: "#999",
      }}>
        <span>{lines} linha{lines !== 1 ? "s" : ""}</span>
        {outputVar && (
          <span style={{
            background: "#E3F2FD", color: "#1565C0",
            borderRadius: 3, padding: "1px 6px", fontWeight: 600,
          }}>
            {"→"} {outputVar}
          </span>
        )}
      </div>

      {/* Success / Error handles */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "flex-end",
          gap: 6, position: "relative", paddingRight: 4,
        }}>
          <span style={{
            fontSize: 9, fontWeight: 700, color: "#fff",
            background: "#4caf50", borderRadius: 4, padding: "1px 8px",
          }}>OK</span>
          <Handle
            type="source" position={Position.Right} id="a"
            className={getHandleClass("source", "a")}
            style={{
              position: "absolute", top: "50%", right: -13,
              transform: "translateY(-50%)",
              cursor: "crosshair",
            }}
            isConnectable={isConnectable}
          />
        </div>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "flex-end",
          gap: 6, position: "relative", paddingRight: 4,
        }}>
          <span style={{
            fontSize: 9, fontWeight: 700, color: "#fff",
            background: "#ef5350", borderRadius: 4, padding: "1px 8px",
          }}>ERRO</span>
          <Handle
            type="source" position={Position.Right} id="error"
            className={getHandleClass("source", "error")}
            style={{
              position: "absolute", top: "50%", right: -13,
              transform: "translateY(-50%)",
              cursor: "crosshair",
            }}
            isConnectable={isConnectable}
          />
        </div>
      </div>
    </NodeWrapper>
  );
});

CodeNode.displayName = "CodeNode";
export default CodeNode;
